import TransferRing from "../components/chat/message/TransferRing";
/* Voice message player shared by chat bubbles and attachment previews. */
import React, { useRef, useState, useEffect } from "react";
import WaveSurfer from "wavesurfer.js";
import { ActionIcon, Box, Flex, Text, useMantineTheme } from "@mantine/core";
import { IconPlayerPlayFilled, IconPlayerPauseFilled } from "@tabler/icons-react";
import { useLang } from "./language";
import { T } from "./translator";
import AudioPlaybackSpeed, { nextPlaybackRate } from "./AudioPlaybackSpeed";
import { claimPlayback, finishPlayback, formatPlaybackTime as formatTime, notifyPlayback, registerPlayback } from "./mediaPlayback";

export default function AudioPlayerWithWaveform({ audioUrl, duration = 0, waveform, uploadProgress, title, date, messageId }) {
	const rootRef = useRef(null);
	const waveformRef = useRef(null);
	const playerRef = useRef(null);
	const metaRef = useRef({});
	metaRef.current = { title, date, messageId };
	const handleRef = useRef(null);
	if (!handleRef.current) {
		const player = () => playerRef.current;
		handleRef.current = {
			kind: "audio",
			getNode: () => rootRef.current,
			getMeta: () => metaRef.current,
			isPlaying: () => Boolean(player()?.isPlaying()),
			play: () => { const p = player(); if (p) { if (p.getCurrentTime() >= p.getDuration()) p.setTime(0); p.play()?.catch?.(() => {}); } },
			pause: () => player()?.pause(),
			getTime: () => player()?.getCurrentTime() || 0,
			getDuration: () => player()?.getDuration() || 0,
			seek: (time) => player()?.setTime(Math.max(0, time)),
			getRate: () => player()?.getPlaybackRate?.() || 1,
			setRate: (rate) => { player()?.setPlaybackRate(rate, true); setPlaybackRate(rate); },
			getVolume: () => player()?.getVolume?.() ?? 1,
			setVolume: (volume) => player()?.setVolume?.(volume)
		};
	}
	const theme = useMantineTheme();
	const { lang } = useLang();
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [totalDuration, setTotalDuration] = useState(Number(duration) || 0);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(false);
	const [playbackRate, setPlaybackRate] = useState(1);
	const dark = theme.colorScheme === "dark";
	const accent = dark ? theme.colors.cyan[3] : theme.colors.cyan[7];
	const muted = dark ? theme.colors.gray[4] : theme.colors.gray[6];

	useEffect(() => registerPlayback(handleRef.current), []);

	useEffect(() => {
		if (!audioUrl || !waveformRef.current) return;
		let disposed = false;
		setLoadError(false);
		setLoading(true);
		setIsPlaying(false);
		setPlaybackRate(1);
		setCurrentTime(0);
		setTotalDuration(Number(duration) || 0);
		const player = WaveSurfer.create({
			container: waveformRef.current,
			waveColor: muted,
			progressColor: accent,
			cursorColor: accent,
			barWidth: 3,
			barGap: 1,
			barRadius: 2,
			cursorWidth: 2,
			dragToSeek: true,
			height: 46,
			normalize: true
		});
		playerRef.current = player;
		player.on("ready", () => {
			if (disposed) return;
			const measuredDuration = player.getDuration();
			if (Number.isFinite(measuredDuration) && measuredDuration > 0) setTotalDuration(measuredDuration);
			setLoading(false);
		});
		player.on("timeupdate", (time) => { if (!disposed) { setCurrentTime(time); notifyPlayback(); } });
		player.on("play", () => { if (!disposed) { setIsPlaying(true); claimPlayback(handleRef.current); } });
		player.on("pause", () => { if (!disposed) { setIsPlaying(false); notifyPlayback(); } });
		player.on("finish", () => { if (!disposed) { setIsPlaying(false); finishPlayback(handleRef.current); } });
		player.load(audioUrl, waveform?.length && duration > 0 ? [waveform] : undefined, duration > 0 ? duration : undefined).catch((error) => {
			if (disposed || error?.name === "AbortError") return;
			setLoadError(true);
			setLoading(false);
		});
		return () => {
			disposed = true;
			playerRef.current = null;
			player.destroy();
		};
	}, [audioUrl, duration, waveform, accent, muted]);

	const toggle = async () => {
		const player = playerRef.current;
		if (!player || loading || loadError) return;
		try {
			if (player.isPlaying()) player.pause();
			else {
				if (player.getCurrentTime() >= player.getDuration()) player.setTime(0);
				await player.play();
			}
		} catch {
			setIsPlaying(false);
		}
	};

	const changeSpeed = () => {
		if (!playerRef.current || loading || loadError) return;
		const next = nextPlaybackRate(playbackRate);
		playerRef.current.setPlaybackRate(next, true);
		setPlaybackRate(next);
	};

	return (
		<Box ref={rootRef} style={{ width: "100%", minWidth: 0, padding: "2px 0" }}>
			{loadError && <Text size="xs" color={muted} role="alert">Audio unavailable</Text>}
				<div style={{ display: loadError ? "none" : undefined }}>
					<Flex align="center" gap={10} style={{ height: 46, minWidth: 0 }}>
						<ActionIcon
							type="button"
							onClick={toggle}
							loading={loading}
							disabled={loading || typeof uploadProgress === 'number'}
							aria-label={isPlaying ? T("pause", lang) : T("play", lang)}
							size={36}
							radius={10}
							style={{ flexShrink: 0, color: accent, background: theme.fn.rgba(accent, 0.14) }}
						>
							{typeof uploadProgress === 'number' && <TransferRing size={36} progress={uploadProgress / 100} />}
                            {isPlaying ? <IconPlayerPauseFilled size={22} /> : <IconPlayerPlayFilled size={22} />}
						</ActionIcon>
						<div ref={waveformRef} style={{ flex: 1, minWidth: 0, width: "100%", overflow: "hidden", touchAction: "pan-y" }} />
						<AudioPlaybackSpeed rate={playbackRate} onClick={changeSpeed} disabled={loading || loadError} color={accent} />
					</Flex>
					<Flex justify="space-between" mt={2} style={{ color: muted, fontSize: 11, fontVariantNumeric: "tabular-nums" }}>
						<span>{totalDuration > 0 ? formatTime(totalDuration) : "—"}</span>
						<span>{formatTime(currentTime)}</span>
					</Flex>
				</div>
		</Box>
	);
}
